"use client";

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

interface SidebarContextType {
  isOpen: boolean;
  isCollapsed: boolean;
  isPinned: boolean;
  toggle: () => void;
  collapse: () => void;
  expand: () => void;
  togglePin: () => void;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

export const SidebarProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isPinned, setIsPinned] = useState(() => {
    if (typeof window === 'undefined') return false;
    const stored = localStorage.getItem('sidebar_pinned');
    return stored === 'true';
  });
  // If not pinned, sidebar should start collapsed
  const [isCollapsed, setIsCollapsed] = useState(() => {
    if (typeof window === 'undefined') return true;
    const storedPinned = localStorage.getItem('sidebar_pinned');
    if (storedPinned !== 'true') {
      return true; // Start collapsed when not pinned
    }
    const stored = localStorage.getItem('sidebar_collapsed');
    return stored === 'true';
  });

  useEffect(() => {
    localStorage.setItem('sidebar_collapsed', String(isCollapsed));
  }, [isCollapsed]);

  useEffect(() => {
    localStorage.setItem('sidebar_pinned', String(isPinned));
  }, [isPinned]);

  const toggle = useCallback(() => setIsOpen((prev) => !prev), []);
  const collapse = useCallback(() => setIsCollapsed(true), []);
  const expand = useCallback(() => setIsCollapsed(false), []);
  const togglePin = useCallback(() => {
    setIsPinned((prev) => !prev);
    if (!isPinned) {
      setIsCollapsed(false);
    }
  }, [isPinned]);

  return (
    <SidebarContext.Provider value={{ isOpen, isCollapsed, isPinned, toggle, collapse, expand, togglePin }}>
      {children}
    </SidebarContext.Provider>
  );
};

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
};
